import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Survey } from "survey-react-ui";
import { Model } from "survey-core";
import "survey-core/defaultV2.min.css";
import { getActiveSurveyBySlug, submitSurveyResponse } from "../surveys/surveyApi";
import { loadTurnstile, renderTurnstile } from "../surveys/turnstile";
import "./SurveyRoute.css";

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: Record<string, unknown>) => string;
      remove: (widgetId: string) => void;
    };
  }
}

const supabaseUrl =
  process.env.REACT_APP_SUPABASE_URL || process.env.VITE_SUPABASE_URL || "";
const turnstileSiteKey =
  process.env.REACT_APP_TURNSTILE_SITE_KEY || process.env.VITE_TURNSTILE_SITE_KEY || "";

const buildMeta = (startedAtMs) => {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get("utm_source") || null,
    utm_medium: params.get("utm_medium") || null,
    utm_campaign: params.get("utm_campaign") || null,
    utm_content: params.get("utm_content") || null,
    utm_term: params.get("utm_term") || null,
    referrer: document.referrer || null,
    locale: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    device: {
      userAgent: navigator.userAgent,
      screen: {
        w: window.screen.width,
        h: window.screen.height,
      },
    },
    started_at: new Date(startedAtMs).toISOString(),
    completion_ms: Date.now() - startedAtMs,
  };
};

const SurveyRoute = () => {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [surveyDefinition, setSurveyDefinition] = useState<Record<string, unknown> | null>(null);
  const [surveyVersionId, setSurveyVersionId] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [submittedId, setSubmittedId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const startedAtRef = useRef<number>(Date.now());
  const submittingRef = useRef<boolean>(false);
  const turnstileTokenRef = useRef<string>("");
  const turnstileWidgetRef = useRef<string | null>(null);
  const turnstileContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let mounted = true;
    const loadSurvey = async () => {
      setLoading(true);
      setError("");
      setSurveyDefinition(null);
      setSurveyVersionId(null);
      try {
        const data = (await getActiveSurveyBySlug(slug)) as {
          definition_json: Record<string, unknown>;
          survey_version_id: string;
        };
        if (!mounted) return;
        setSurveyDefinition(data.definition_json);
        setSurveyVersionId(data.survey_version_id);
        startedAtRef.current = Date.now();
        turnstileTokenRef.current = "";
      } catch (err) {
        if (!mounted) return;
        setError(err.message || "Unable to load survey.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    if (slug) {
      loadSurvey();
    } else {
      setLoading(false);
      setError("Missing survey slug.");
    }

    return () => {
      mounted = false;
    };
  }, [slug]);

  useEffect(() => {
    if (!turnstileSiteKey || !turnstileContainerRef.current) return;

    let cancelled = false;
    loadTurnstile()
      .then(() => {
        if (cancelled || !turnstileContainerRef.current) return;
        turnstileWidgetRef.current = renderTurnstile({
          siteKey: turnstileSiteKey,
          container: turnstileContainerRef.current,
          onVerify: (token) => {
            turnstileTokenRef.current = token;
            setError("");
          },
          onExpire: () => {
            turnstileTokenRef.current = "";
          },
        });
      })
      .catch(() => {
        setError("Unable to load spam protection.");
      });

    return () => {
      cancelled = true;
      if (window.turnstile && turnstileWidgetRef.current) {
        window.turnstile.remove(turnstileWidgetRef.current);
      }
    };
  }, [surveyVersionId]);

  const onSubmitSurvey = useCallback(
    async (sender) => {
      if (submittingRef.current) return;
      if (!surveyVersionId) {
        setError("Survey version is missing.");
        return;
      }
      submittingRef.current = true;
      setSubmitting(true);
      setError("");
      try {
        const meta = buildMeta(startedAtRef.current);
        const payload = await submitSurveyResponse({
          supabaseUrl,
          surveyVersionId,
          answers: sender.data,
          meta,
          turnstileToken: turnstileTokenRef.current || undefined,
        });
        setSubmittedId(payload.response_id || "submitted");
      } catch (err) {
        setError(err.message || "Submission failed.");
      } finally {
        submittingRef.current = false;
        setSubmitting(false);
      }
    },
    [surveyVersionId]
  );

  const model = useMemo(() => {
    if (!surveyDefinition) return null;
    const surveyModel = new Model(surveyDefinition);
    surveyModel.showProgressBar = "top";
    surveyModel.showQuestionNumbers = "on";
    surveyModel.completeText = "Submit";
    surveyModel.onCompleting.add((_, options) => {
      if (turnstileSiteKey && !turnstileTokenRef.current) {
        options.allowComplete = false;
        setError("Please complete the spam check before submitting.");
      }
    });
    surveyModel.onComplete.add(onSubmitSurvey);
    return surveyModel;
  }, [surveyDefinition, onSubmitSurvey]);

  if (loading) {
    return <div className="survey-shell">Loading survey...</div>;
  }

  if (submittedId) {
    return (
      <div className="survey-shell survey-success">
        <h2>Thanks for your feedback!</h2>
        <p>Your response has been recorded.</p>
      </div>
    );
  }

  if (!surveyDefinition) {
    return <div className="survey-shell survey-error">{error || "Survey unavailable."}</div>;
  }

  return (
    <div className="survey-shell">
      {error ? <div className="survey-error">{error}</div> : null}
      {turnstileSiteKey ? (
        <div className="survey-turnstile">
          <div ref={turnstileContainerRef} />
        </div>
      ) : null}
      {model ? <Survey model={model} /> : null}
      {submitting ? <div className="survey-submitting">Submitting...</div> : null}
    </div>
  );
};

export default SurveyRoute;
